import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useWorkspaceScope } from '@/contexts/useWorkspaceScope';
import { useEntitlements } from '@/hooks/useEntitlements';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Building2, Sparkles, Loader2, Check } from 'lucide-react';
import { toast } from 'sonner';
import { errorMessage, toError } from '@/lib/errors';

interface CompleteProSetupModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CompleteProSetupModal({
  open,
  onOpenChange,
}: CompleteProSetupModalProps) {
  const { workspaceLimit, plan } = useEntitlements();
  const { workspaces, refetchWorkspaces, setActiveWorkspace } = useWorkspaceScope();
  const queryClient = useQueryClient();
  const [isProvisioning, setIsProvisioning] = useState(false);

  const provisionWorkspaces = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.rpc('ensure_pro_workspaces');

      if (error) {
        throw toError(error, 'Failed to provision workspaces');
      }

      if (!data || !(data as any).success) {
        throw new Error('Failed to provision workspaces');
      }

      return data as {
        success: boolean;
        created: number;
        workspaces: Array<{ id: string; name: string }>;
      };
    },
    onSuccess: async (data) => {
      if (data.created > 0) {
        toast.success(`Created ${data.created} workspace${data.created !== 1 ? 's' : ''}: Business 1 and Business 2`);
      } else {
        toast.success('Workspaces already set up');
      }

      // Invalidate and refetch workspace list
      queryClient.invalidateQueries({ queryKey: ['my-workspaces'], exact: false });
      await refetchWorkspaces();
      
      // Mark as completed in localStorage
      const { data: { user } } = await supabase.auth.getUser();
      if (user?.id) {
        localStorage.setItem(`pro_setup_done:${user.id}`, 'true');
      }

      // Optionally switch to first new workspace if created
      if (data.created > 0 && data.workspaces.length > 0) {
        // Find Business 1 or Business 2
        const newWorkspace = data.workspaces.find(
          w => w.name === 'Business 1' || w.name === 'Business 2'
        );
        if (newWorkspace) {
          setActiveWorkspace(newWorkspace.id);
        }
      }

      onOpenChange(false);
    },
    onError: (error: unknown) => {
      console.error('[CompleteProSetupModal] ensure_pro_workspaces error:', error);
      toast.error(errorMessage(error, 'Failed to provision workspaces'));
      setIsProvisioning(false);
    },
  });

  const handleProvision = async () => {
    setIsProvisioning(true);
    provisionWorkspaces.mutate();
  };

  const currentCount = workspaces.length;
  const canProvision = workspaceLimit === 3 && currentCount < 3;

  if (!canProvision) {
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
              <Sparkles className="w-6 h-6 text-primary" />
            </div>
            <div>
              <DialogTitle className="text-xl">Complete Pro Setup</DialogTitle>
              <DialogDescription className="mt-1">
                Set up your additional workspaces
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <p className="text-sm text-muted-foreground">
            With {plan === 'pro' ? 'Pro' : 'Base'}, you can organize your sales pipeline across multiple workspaces.
          </p>

          <div className="space-y-2">
            <div className="flex items-center gap-3 p-3 rounded-lg border">
              <Building2 className="w-5 h-5 text-muted-foreground flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-medium">Current workspaces</p>
                <p className="text-xs text-muted-foreground">
                  {currentCount} of {workspaceLimit} workspaces
                </p>
              </div>
            </div>

            <div className="p-4 rounded-lg bg-primary/5 border border-primary/20">
              <p className="text-sm font-medium mb-2">Will be created:</p>
              <ul className="space-y-1.5">
                <li className="flex items-center gap-2 text-sm">
                  <Check className="w-4 h-4 text-primary" />
                  <span>Business 1</span>
                </li>
                <li className="flex items-center gap-2 text-sm">
                  <Check className="w-4 h-4 text-primary" />
                  <span>Business 2</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2">
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={isProvisioning}
          >
            Skip
          </Button>
          <Button
            onClick={handleProvision}
            disabled={isProvisioning}
          >
            {isProvisioning && (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            )}
            Create Business 1 + Business 2
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

